import { UserState } from "core/state/userState";
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { User, UserManager } from "oidc-client-ts";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { authenticateMobileUser, flutterAppOpen } from "core/javascriptChannels";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { appInsightsLogger } from "core/monitoring/AppInsights";

export type TokenRenewalContextType = {
  renewToken: () => void;
};

type TokenRenewalContextProviderProps = {
  userManager: UserManager;
};

export const TokenRenewalContext = createContext<TokenRenewalContextType>({} as TokenRenewalContextType);

export const useTokenRenewalContext = () => useContext(TokenRenewalContext);

export const TokenRenewalContextProvider = (props: PropsWithChildren & TokenRenewalContextProviderProps) => {
  const [startRefresh, setStartRefresh] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const { isMobileApp } = useMobilePlatform();

  useEffect(() => {
    const renewToken = async () => {
      function tokenNotExpiringSoon(odicUser: User) {
        return odicUser.expires_in != null && odicUser.expires_in > 300;
      }

      function tokenNotExpired(odicUser: User) {
        return !odicUser.expired;
      }

      const updateUserTokens = async () => {
        let overridden = false;
        const userAuth = await flutterAppOpen();
        if (userAuth) {
          const user = await props.userManager.getUser();
          const flutterUser = User.fromStorageString(userAuth);
          if (user != null) {
            if (user.access_token !== flutterUser.access_token || user.refresh_token !== flutterUser.refresh_token) {
              overridden = true;
              user.access_token = flutterUser.access_token;
              user.refresh_token = flutterUser.refresh_token;
              await props.userManager.storeUser(user);
              await props.userManager.getUser(); // Triggers user loaded event to update auth state
            }
          } else {
            overridden = true;
            await props.userManager.storeUser(flutterUser);
            await props.userManager.getUser();
          }
        }
        return overridden;
      };

      try {
        const user = UserState.get();
        if (user) {
          const odicUser = await props.userManager.getUser();
          if (odicUser !== null && (tokenNotExpired(odicUser) || tokenNotExpiringSoon(odicUser))) return;

          const overrideWithFlutterTokens = await updateUserTokens();
          if (overrideWithFlutterTokens) return;
          await props.userManager.signinSilent();
          const userAfter = await props.userManager.getUser();

          if (isMobileApp) {
            const clientCode = ClientInfoState.getCode();
            if (userAfter && clientCode) {
              authenticateMobileUser(clientCode, userAfter?.toStorageString());
            }
          }
        }
      } catch (err) {
        appInsightsLogger.error("Error from signinSilent on expiry:", err);
      } finally {
        setIsLocked(false);
        setStartRefresh(false);
      }
    };

    if (startRefresh && !isLocked) {
      setIsLocked(true);
      renewToken();
    } else {
      setStartRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, startRefresh]);

  return <TokenRenewalContext.Provider value={{ renewToken: () => setStartRefresh(true) }}>{props.children}</TokenRenewalContext.Provider>;
};
